// <OrgCardGrid> — Org View Step 5 (T-051, spec §6.2 / §6.3).
//
// Renders the employee cards in a responsive grid, or the centered
// empty-state block when the active filter combination matches zero
// employees.

import { Users } from 'lucide-react';
import type { OrgViewEmployee } from '@pa/shared';
import { OrgEmployeeCard } from './OrgEmployeeCard';
import type { OrgViewerRole } from './OrgEmployeeCardMenu';
import { DENSITY_CLASSES, type OrgDensity } from './OrgDensityToggle';

export interface OrgCardGridProps {
  employees: OrgViewEmployee[];
  viewerRole: OrgViewerRole;
  onOpenProfile: (id: string) => void;
  onQuickEdit: (id: string) => void;
  onSendMessage: (employee: OrgViewEmployee) => void;
  onDeactivate: (employee: OrgViewEmployee) => void;
  onClearFilters: () => void;
  onAddEmployee: () => void;
  /** True when the viewer is OWNER or ADMIN — controls the
   *  "+ Add Employee" CTA on the empty state (HR-only per spec §6.3). */
  canAddEmployee: boolean;
  /** Spec §6.1.3 — controls grid columns + card padding + avatar size. */
  density?: OrgDensity;
}

export function OrgCardGrid({
  employees,
  viewerRole,
  onOpenProfile,
  onQuickEdit,
  onSendMessage,
  onDeactivate,
  onClearFilters,
  onAddEmployee,
  canAddEmployee,
  density = 'comfortable',
}: OrgCardGridProps) {
  const densityClasses = DENSITY_CLASSES[density];
  if (employees.length === 0) {
    return (
      <div
        data-testid="org-empty-state"
        className="flex flex-col items-center justify-center py-24 text-center"
      >
        <Users size={48} aria-hidden className="text-slate-300 mb-4" />
        <p className="text-base font-semibold text-slate-600">
          No people match your filters
        </p>
        <p className="text-sm text-slate-400 mt-1">
          Try clearing one or more filters, or add the person yourself.
        </p>
        <div className="flex items-center gap-3 mt-4">
          <button
            type="button"
            onClick={onClearFilters}
            data-testid="org-empty-state-clear"
            className="text-sm font-bold text-[#6344C6] hover:underline px-3 py-2"
          >
            Clear all filters
          </button>
          {canAddEmployee ? (
            <button
              type="button"
              onClick={onAddEmployee}
              data-testid="org-empty-state-add"
              className="bg-[#6344C6] text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-[#553ABE] active:scale-95 shadow-md hover:shadow-lg transition"
            >
              + Add Employee
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="org-card-grid"
      data-density={density}
      className={`grid ${densityClasses.grid} gap-6`}
    >
      {employees.map((emp) => (
        <OrgEmployeeCard
          key={emp.id}
          employee={emp}
          viewerRole={viewerRole}
          onOpenProfile={onOpenProfile}
          onQuickEdit={onQuickEdit}
          onSendMessage={onSendMessage}
          onDeactivate={onDeactivate}
          density={density}
        />
      ))}
    </div>
  );
}
